import { useState, useEffect } from "react";

const CONSENT_KEY = "cookie_consent_v1";

export type ConsentState = {
  necessary: true;       // Immer aktiv, nicht abwählbar
  analytics: boolean;    // Umami Analytics
  fonts: boolean;        // Google Fonts
  decidedAt: string;
};

export function getConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentState;
  } catch {
    return null;
  }
}

export function hasConsent(): boolean {
  return getConsent() !== null;
}

export function hasAnalyticsConsent(): boolean {
  return getConsent()?.analytics === true;
}

export function hasFontsConsent(): boolean {
  return getConsent()?.fonts === true;
}

function saveConsent(analytics: boolean, fonts: boolean) {
  const consent: ConsentState = {
    necessary: true,
    analytics,
    fonts,
    decidedAt: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  // Analytics-Script dynamisch laden wenn Consent gegeben
  if (analytics) {
    loadAnalytics();
  }
  // Google Fonts nicht mehr nötig – Schriften sind lokal über CDN eingebettet
}

function loadAnalytics() {
  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;
  if (!endpoint || !websiteId || document.getElementById("umami-script")) return;
  const script = document.createElement("script");
  script.id = "umami-script";
  script.defer = true;
  script.src = `${endpoint}/umami`;
  script.setAttribute("data-website-id", websiteId);
  document.head.appendChild(script);
}

// Google Fonts werden nicht mehr benötigt – Schriften sind lokal über CDN eingebettet (index.css)
// function loadGoogleFonts() { ... }  ← entfernt

// Beim App-Start: bereits gegebenen Consent sofort anwenden
export function applyStoredConsent() {
  const consent = getConsent();
  if (!consent) return;
  if (consent.analytics) loadAnalytics();
  // Google Fonts nicht mehr nötig – Schriften lokal eingebettet
}

// ─── Banner-Komponente ────────────────────────────────────────────────────────
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [fonts, setFonts] = useState(false);

  useEffect(() => {
    if (!hasConsent()) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent(true, true);
    setVisible(false);
  };

  const acceptNecessary = () => {
    saveConsent(false, false);
    setVisible(false);
  };

  const saveCustom = () => {
    saveConsent(analytics, fonts);
    setVisible(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: "#111111",
        borderTop: "3px solid #6d0000",
        color: "#f5f5f5",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "14px",
        padding: "20px 24px",
        boxShadow: "0 -4px 24px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        {/* Haupttext */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 400px" }}>
            <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: "15px", color: "#ffffff" }}>
              🍪 Datenschutz & Cookies
            </p>
            <p style={{ margin: 0, color: "#cccccc", lineHeight: 1.5 }}>
              Wir verwenden technisch notwendige Cookies für den Betrieb dieser Website. Mit deiner Zustimmung laden wir außerdem Umami Analytics (datenschutzfreundlich, keine personenbezogenen Daten, kein Tracking über Websites hinweg). Mehr dazu in unserer{" "}
              <a href="/datenschutz" style={{ color: "#ff9999", textDecoration: "underline" }}>
                Datenschutzerklärung
              </a>
              .
            </p>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: "200px" }}>
            <button
              onClick={acceptNecessary}
              style={{
                backgroundColor: "transparent",
                color: "#cccccc",
                border: "1px solid #555",
                padding: "10px 20px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "13px",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              Nur notwendige
            </button>
            <button
              onClick={acceptAll}
              style={{
                backgroundColor: "transparent",
                color: "#ffffff",
                border: "1px solid #888",
                padding: "10px 20px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "13px",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              Alle akzeptieren
            </button>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={{
                backgroundColor: "transparent",
                color: "#888888",
                border: "none",
                padding: "4px 0",
                cursor: "pointer",
                fontSize: "12px",
                textDecoration: "underline",
                textAlign: "left",
              }}
            >
              {showDetails ? "Details ausblenden" : "Einstellungen anpassen"}
            </button>
          </div>
        </div>

        {/* Detail-Einstellungen */}
        {showDetails && (
          <div
            style={{
              marginTop: "16px",
              paddingTop: "16px",
              borderTop: "1px solid #333",
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            {/* Notwendige */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", flex: "1 1 200px", cursor: "not-allowed" }}>
              <input type="checkbox" checked disabled style={{ marginTop: "3px", accentColor: "#6d0000" }} />
              <div>
                <span style={{ fontWeight: 600, color: "#ffffff", display: "block" }}>Notwendige Cookies</span>
                <span style={{ color: "#888", fontSize: "12px" }}>Session-Verwaltung, Authentifizierung. Immer aktiv.</span>
              </div>
            </label>

            {/* Analytics */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", flex: "1 1 200px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                style={{ marginTop: "3px", accentColor: "#6d0000" }}
              />
              <div>
                <span style={{ fontWeight: 600, color: "#ffffff", display: "block" }}>Umami Analytics</span>
                <span style={{ color: "#888", fontSize: "12px" }}>
                  Anonyme Seitenaufrufe (kein Tracking, keine personenbezogenen Daten, DSGVO-konform).
                </span>
              </div>
            </label>

            <button
              onClick={saveCustom}
              style={{
                alignSelf: "flex-end",
                backgroundColor: "#115751",
                color: "#ffffff",
                border: "none",
                padding: "10px 20px",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "13px",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              Auswahl speichern
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
